import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { FiMail, FiPhone, FiMapPin, FiSend, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { GiCoral } from 'react-icons/gi';
import { Input, TextArea, Select, Button, Card, WaveBackground } from '../styles/GlobalStyles';

const PageHeader = styled(WaveBackground)`
  background: ${props => props.theme.gradients.ocean};
  color: white;
  padding: 80px 0 60px;
  text-align: center;
`;

const HeaderContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 2.5rem;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const PageDescription = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
  line-height: 1.6;
`;

const ContentSection = styled.section`
  padding: 80px 0;
  background: ${props => props.theme.colors.background};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 40px 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 0 15px;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  margin-bottom: 4rem;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: 1.5rem;
    margin-bottom: 2rem;
  }
`;

const ContactForm = styled(Card)`
  padding: 3rem;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 2.5rem;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 1.5rem;
  }
`;

const ContactInfo = styled(Card)`
  padding: 3rem;
  background: ${props => props.theme.gradients.wave};
  color: white;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 2.5rem;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 1.5rem;
  }
`;

const FormTitle = styled.h2`
  color: ${props => props.theme.colors.primary};
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 2rem;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.6rem;
    margin-bottom: 1.5rem;
  }
`;

const InfoTitle = styled.h2`
  color: white;
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 2rem;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.6rem;
    margin-bottom: 1.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    margin-bottom: 1rem;
  }
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
`;

const Required = styled.span`
  color: ${props => props.theme.colors.error};
`;

const ContactInfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  color: white;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    margin-bottom: 1.5rem;
    align-items: flex-start;
  }

  .info-icon {
    font-size: 1.5rem;
    opacity: 0.9;
    flex-shrink: 0;

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      font-size: 1.3rem;
      margin-top: 0.1rem;
    }
  }

  .info-content {
    .info-title {
      font-weight: 600;
      margin-bottom: 0.25rem;

      @media (max-width: ${props => props.theme.breakpoints.mobile}) {
        font-size: 0.95rem;
      }
    }

    .info-text {
      opacity: 0.9;
      line-height: 1.5;

      @media (max-width: ${props => props.theme.breakpoints.mobile}) {
        font-size: 0.9rem;
        line-height: 1.4;
      }
    }
  }
`;

const SubmitButton = styled(Button)`
  width: 100%;
  justify-content: center;
  margin-top: 1rem;
`;

const Message = styled.div`
  padding: 1rem 1.5rem;
  border-radius: ${props => props.theme.borderRadius.medium};
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 500;

  ${props => props.type === 'success' && `
    background: rgba(16, 185, 129, 0.1);
    color: ${props.theme.colors.success};
    border: 1px solid rgba(16, 185, 129, 0.2);
  `}

  ${props => props.type === 'error' && `
    background: rgba(239, 68, 68, 0.1);
    color: ${props.theme.colors.error};
    border: 1px solid rgba(239, 68, 68, 0.2);
  `}
`;

const ItemReference = styled.div`
  background: ${props => props.theme.colors.backgroundSecondary};
  padding: 1.5rem;
  border-radius: ${props => props.theme.borderRadius.medium};
  margin-bottom: 1.5rem;
  border-left: 4px solid ${props => props.theme.colors.primary};

  .item-title {
    font-weight: 600;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .item-name {
    color: ${props => props.theme.colors.text};
    font-size: 1.1rem;
  }

  .item-type {
    font-size: 0.9rem;
    color: ${props => props.theme.colors.textLight};
  }
`;

const BottomInfoCard = styled(Card)`
  padding: 2rem;
  text-align: center;
  background: linear-gradient(45deg, #e3f2fd, #f8fdff);

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 1.5rem;
  }
`;

const BottomInfoTitle = styled.h3`
  color: #006994;
  margin-bottom: 1rem;
  font-size: 1.5rem;
  font-weight: 600;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.3rem;
  }
`;

const BottomInfoText = styled.p`
  color: #64748b;
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 0.95rem;
    line-height: 1.5;
  }
`;

const Contact = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    item_type: 'Allgemein',
    item_id: null
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [referencedItem, setReferencedItem] = useState(null);

  useEffect(() => {
    // Scroll to top when component mounts (especially when navigating from other pages)
    window.scrollTo(0, 0);
    
    // Check if we have item data from navigation state
    if (location.state && location.state.item) {
      const { item, type } = location.state;
      setReferencedItem({ item, type });
      setFormData(prev => ({
        ...prev,
        subject: `Interesse an ${type}: ${item.name}`,
        item_type: type,
        item_id: item.id,
        message: `Hallo,\n\nich interessiere mich für ${type === 'Koralle' ? 'die' : 'den'} ${type} "${item.name}".\n\nBitte kontaktieren Sie mich für weitere Informationen.\n\nVielen Dank!`
      }));
    }
  }, [location.state]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await axios.post('/api/contact', formData);
      setMessage({
        type: 'success',
        text: 'Ihre Nachricht wurde erfolgreich gesendet! Wir werden uns bald bei Ihnen melden.'
      });

      // Reset form if not referencing a specific item
      if (!referencedItem) {
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          item_type: 'Allgemein',
          item_id: null
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Fehler beim Senden der Nachricht. Bitte versuchen Sie es erneut.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader>
        <HeaderContent>
          <PageTitle>
            <FiMail size={60} />
            Kontakt
          </PageTitle>
          <PageDescription>
            Interesse an einem meiner überschüssigen Meereslebewesen? 
            Kontaktieren Sie mich gerne – vielleicht wird gerade das Richtige zu viel!
          </PageDescription>
        </HeaderContent>
      </PageHeader>

      <ContentSection>
        <Container>
          <ContactGrid>
            <ContactForm>
              <FormTitle>Nachricht senden</FormTitle>

              {referencedItem && (
                <ItemReference>
                  <div className="item-title">
                    <GiCoral size={20} />
                    Interesse an:
                  </div>
                  <div className="item-name">{referencedItem.item.name}</div>
                  <div className="item-type">{referencedItem.type}</div>
                </ItemReference>
              )}

              {message.text && (
                <Message type={message.type}>
                  {message.type === 'success' ? <FiCheck size={20} /> : <FiAlertCircle size={20} />}
                  {message.text}
                </Message>
              )}

              <form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label>
                    Name <Required>*</Required>
                  </Label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Ihr vollständiger Name"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>
                    E-Mail <Required>*</Required>
                  </Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="ihre.email@beispiel.de"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Betreff</Label>
                  <Input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="Worum geht es in Ihrer Nachricht?"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Kategorie</Label>
                  <Select
                    name="item_type"
                    value={formData.item_type}
                    onChange={handleInputChange}
                  >
                    <option value="Allgemein">Allgemeine Anfrage</option>
                    <option value="Koralle">Interesse an Koralle</option>
                    <option value="Fisch">Interesse an Fisch</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>
                    Nachricht <Required>*</Required>
                  </Label>
                  <TextArea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Beschreiben Sie Ihr Anliegen, Fragen oder Ihr Interesse..."
                    required
                    rows={6}
                  />
                </FormGroup>

                <SubmitButton type="submit" disabled={loading}>
                  <FiSend size={18} />
                  {loading ? 'Wird gesendet...' : 'Nachricht senden'}
                </SubmitButton>
              </form>
            </ContactForm>

            <ContactInfo>
              <InfoTitle>Kontaktinformationen</InfoTitle>

              <ContactInfoItem>
                <FiMail className="info-icon" />
                <div className="info-content">
                  <div className="info-title">E-Mail</div>
                  <div className="info-text">
                    riffzauber@gmx.ch<br />
                    Antwort innerhalb von 24 Stunden
                  </div>
                </div>
              </ContactInfoItem>

              <ContactInfoItem>
                <FiMapPin className="info-icon" />
                <div className="info-content">
                  <div className="info-title">Standort</div>
                  <div className="info-text">
                    Raum Zürich, Schweiz<br />
                    Besichtigung und Abholung nur nach Vereinbarung
                  </div>
                </div>
              </ContactInfoItem>

              <div style={{ marginTop: '3rem', padding: '1.5rem', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '12px' }}>
                <h3 style={{ marginBottom: '1rem', fontSize: '1.2rem' }}>Besichtigungen</h3>
                <div style={{ fontSize: '0.95rem', lineHeight: '1.6', opacity: '0.9' }}>
                  <strong>Nur nach Terminvereinbarung</strong><br />
                  Bitte kontaktieren Sie mich vorher per E-Mail oder Telefon, 
                  um einen passenden Termin für Besichtigung und Abholung zu finden.<br />
                  <em style={{ fontSize: '0.85rem', marginTop: '0.5rem', display: 'block' }}>
                    Flexible Zeiten je nach Verfügbarkeit
                  </em>
                </div>
              </div>
            </ContactInfo>
          </ContactGrid>

          <BottomInfoCard>
            <BottomInfoTitle>
              Warum mich kontaktieren?
            </BottomInfoTitle>
            <BottomInfoText>
              Ich halte schon lange Fische, aber Salzwasser ist noch relativ neu für mich. Ich bin immer wieder 
              überrascht, wie schnell meine Korallen und Fische wachsen! Ich liebe es, ihnen ein gutes neues 
              Zuhause bei Menschen zu finden, die sie genauso gut pflegen werden.
            </BottomInfoText>
          </BottomInfoCard>
        </Container>
      </ContentSection>
    </>
  );
};

export default Contact;

